import React from "react";

/**
 * Phase B — Beam Live 2.0 UI controls.
 *
 * Two tiny presentational components extracted so they can be real-
 * rendered under @testing-library/react without pulling the entire
 * LiveRoomPage (LiveKit + PlayerProvider + WebSocket + Router) into
 * Jest. Visibility rules match the backend `/boxes/*` guards —
 * backend remains authoritative; this is just the UI surface.
 */

export const BeamBoxLockToggle = ({
  canModerate, boxesLocked, isLive, onToggle,
}) => {
  if (!isLive) return null;
  if (!canModerate) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid="beam-box-lock-toggle"
      className={`text-[11px] font-bold px-3 py-1 rounded-full border transition-all ${
        boxesLocked
          ? "bg-amber-500/20 text-amber-100 border-amber-400/50 hover:bg-amber-500/30"
          : "bg-emerald-500/20 text-emerald-100 border-emerald-400/50 hover:bg-emerald-500/30"
      }`}
      aria-pressed={boxesLocked}
    >
      {boxesLocked ? "🔒 Unlock Boxes" : "🔓 Lock Boxes"}
    </button>
  );
};

/**
 * Replaces the inert "Open" placeholder.
 *   • Participant eligible + unlocked → Join Box CTA.
 *   • Participant eligible + locked → "Locked" indicator.
 *   • Host / existing speaker / banned / full → inert "Open" chip (no CTA),
 *     because backend would 400/403/409 them anyway.
 */
export const BeamBoxEmptySlot = ({
  slotIndex, boxesLocked, isHost, meIsSpeaker, meIsBanned,
  stageFull, isLive, isAuthed, onJoin, isJoiningBox,
}) => {
  const canJoin =
    isLive && isAuthed && !isHost && !meIsSpeaker && !meIsBanned
    && !stageFull && !boxesLocked;

  if (boxesLocked) {
    return (
      <div
        key={`beam-slot-locked-${slotIndex}`}
        data-testid={`beam-box-locked-slot-${slotIndex}`}
        className="aspect-square rounded-2xl border-2 border-dashed border-amber-400/30 bg-amber-500/[0.04] flex flex-col items-center justify-center text-amber-200/70 text-xs font-medium"
      >
        <span className="text-base leading-none">🔒</span>
        <span className="mt-1">Locked</span>
      </div>
    );
  }

  if (canJoin) {
    // Phase B mobile-UX — Immediate "Joining…" feedback. The parent
    // sets `isJoiningBox` the moment any join tap starts, so EVERY
    // open-slot button on the stage flips to the pending state at
    // once (prevents the user tapping a different slot while the
    // first REST call is still in flight). Cleared by the parent on
    // success OR failure.
    const pending = !!isJoiningBox;
    return (
      <button
        key={`beam-slot-join-${slotIndex}`}
        type="button"
        onClick={pending ? undefined : onJoin}
        disabled={pending}
        aria-busy={pending}
        data-testid={`beam-box-join-btn-${slotIndex}`}
        data-pending={pending ? "true" : "false"}
        className={`aspect-square rounded-2xl border-2 border-dashed flex flex-col items-center justify-center text-xs font-bold transition-all ${
          pending
            ? "border-amber-300/60 bg-amber-500/10 text-amber-100 cursor-wait animate-pulse"
            : "border-fuchsia-400/60 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-100"
        }`}
      >
        <span className="text-base leading-none">{pending ? "⏳" : "🪑"}</span>
        <span className="mt-1">{pending ? "Joining…" : "Join Box"}</span>
      </button>
    );
  }

  return (
    <div
      key={`beam-slot-empty-${slotIndex}`}
      data-testid={`beam-box-empty-slot-${slotIndex}`}
      className="aspect-square rounded-2xl border-2 border-dashed border-purple-400/15 bg-purple-500/[0.03] flex items-center justify-center text-purple-300/40 text-xs font-medium animate-in fade-in"
    >
      <span>Open</span>
    </div>
  );
};

export default BeamBoxEmptySlot;
